(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('OleaConfig')?.textContent || '{}');
  window.Olea = {
    ...config,
    watchMedia(media, callback, { signal } = {}) {
      let mode = media.matches;
      let timer;
      media.addEventListener('change', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (signal?.aborted || mode === media.matches) return;
          mode = media.matches;
          callback();
        }, 120);
      }, { signal });
      signal?.addEventListener('abort', () => clearTimeout(timer), { once: true });
    },
    createIcon(name) {
      return document.getElementById(`OleaIcon-${name}`)?.content.firstElementChild?.cloneNode(true) || null;
    },
    money(cents) {
      return new Intl.NumberFormat(config.locale || 'fr', {
        style: 'currency', currency: config.currency || 'EUR', minimumFractionDigits: cents % 100 === 0 ? 0 : 2
      }).format(cents / 100);
    },
    toast(message) {
      const element = document.getElementById('OleaToast');
      element?.classList.remove('is-visible');
      this.announce(message);
    },
    announce(message) {
      const element = document.getElementById('OleaLive');
      if (element) element.textContent = message;
    },
    async request(url, options = {}) {
      const response = await fetch(url, { ...options, headers: { Accept: 'application/json', ...(options.headers || {}) } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || config.strings.error);
      return data;
    },
    async copyText(text) {
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        const focus = document.activeElement;
        const field = document.createElement('textarea');
        field.value = text;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
        document.body.append(field);
        field.select();
        let copied = false;
        try { copied = document.execCommand('copy'); } finally { field.remove(); focus?.focus({ preventScroll: true }); }
        if (!copied) throw new Error('Copy unavailable');
      }
    }
  };

  class CopyCode extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const button = this.querySelector('button');
      if (!button) return;
      const label = this.querySelector('[data-copy-label]') || button;
      const originalNodes = Array.from(label.childNodes, node => node.cloneNode(true));
      const originalAriaLabel = button.getAttribute('aria-label');
      const restore = () => {
        label.replaceChildren(...originalNodes.map(node => node.cloneNode(true)));
        if (originalAriaLabel === null) button.removeAttribute('aria-label');
        else button.setAttribute('aria-label', originalAriaLabel);
        delete this.dataset.copied;
        button.disabled = false;
      };
      this.restoreCopy = restore;
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        button.disabled = true;
        try {
          await window.Olea.copyText(this.dataset.code || '');
          if (!this.isConnected || this.events?.signal.aborted) return;
          label.textContent = `${config.strings.copied} ✓`;
          button.setAttribute('aria-label', config.strings.copied);
          this.dataset.copied = 'true';
          window.Olea.announce(config.strings.copied);
          clearTimeout(this.copyResetTimer);
          this.copyResetTimer = setTimeout(restore, 3000);
        } catch {
          restore();
          const code = this.querySelector('[data-code-text]');
          if (!code) return;
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(code);
          selection.removeAllRanges();
          selection.addRange(range);
          code.focus();
        }
      }, { signal: this.events.signal });
    }
    disconnectedCallback() { this.events?.abort(); this.events = null; clearTimeout(this.copyResetTimer); this.restoreCopy?.(); }
  }
  if (!customElements.get('copy-code')) customElements.define('copy-code', CopyCode);

  class PromoCountdown extends HTMLElement {
    connectedCallback() {
      const end = Date.parse(this.dataset.end);
      if (!Number.isFinite(end)) { this.hidden = true; return; }
      const update = () => {
        const rest = end - Date.now();
        if (rest <= 0) { this.hidden = true; clearInterval(this.timer); return; }
        const seconds = Math.floor(rest / 1000), hours = Math.floor(seconds / 3600);
        this.textContent = [Math.floor(hours / 24), String(hours % 24).padStart(2, '0'), String(Math.floor(seconds / 60) % 60).padStart(2, '0'), String(seconds % 60).padStart(2, '0')].join(' : ');
      };
      update();
      this.timer = setInterval(update, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  if (!customElements.get('promo-countdown')) customElements.define('promo-countdown', PromoCountdown);

  if (!matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
    }), { rootMargin: '0px 0px -30px' });
    const init = (scope = document) => scope.querySelectorAll('[data-reveal]').forEach(element => {
      if (element.getBoundingClientRect().top > innerHeight) { element.classList.add('will-reveal'); observer.observe(element); }
    });
    init();
    document.addEventListener('shopify:section:load', event => init(event.target));
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-dialog-close]');
    const dialog = button?.closest('dialog');
    if (dialog) window.OleaOverlayMotion?.hide(dialog) ?? dialog.close();
  });
})();
